import { Country } from '../types/Country';
import { DemoRequest } from '../types/DemoRequest';
import { ApiService } from './apiService';

export class AuthService {
  private apiService = new ApiService();

  async dummy(): Promise<any> {
    await this.apiService.get('/api/auth/dummy', {
      authenticate: false,
    });

    return null;
  }

  async getLoginFields(): Promise<Record<string, any>> {
    const response = await this.apiService.get('/api/auth/login_fields', {
      authenticate: false,
    });
    const json = response.data as Record<string, any>;

    const data = {
      isPhoneUnique: json['phone_unique'] as boolean,
      isEmailUnique: json['email_unique'] as boolean,
      loginWithOtp: json['login_with_otp'] as boolean,
      countries: (json['countries'] as Array<any>).map((e) =>
        Country.fromJson(e)
      ),
    };
    return data;
  }

  async getResetPasswordField(): Promise<Record<string, any>> {
    const response = await this.apiService.get(
      '/api/auth/reset_password_field',
      {
        authenticate: false,
      }
    );
    const json = response.data as Record<string, any>;

    const data = {
      passwordPolicyEnabled: json['password_policy'] as boolean,
      resetPasswordBasedOn: json['reset_password_based_on'] as string,
      countries: (json['countries'] as Array<any>).map((e) =>
        Country.fromJson(e)
      ),
    };
    return data;
  }

  async sendLoginOtp(
    loginUsing: string,
    params: {
      email?: string;
      phone?: string;
      phoneCode?: string;
    }
  ): Promise<void> {
    const data: Record<string, string> = {};
    if (loginUsing === 'email') {
      data['login_using'] = 'email';
      if (params.email) {
        data['email'] = params.email;
      }
    }
    if (loginUsing === 'phone') {
      data['login_using'] = 'mobile_code';
      if (params.phone) {
        data['mobile'] = params.phone;
      }
      if (params.phoneCode) {
        data['mobile_code'] = params.phoneCode;
      }
    }

    await this.apiService.post('/api/auth/send_otp', data, {
      authenticate: false,
    });
  }

  async sendResetOtp(
    resetUsing: string,
    params: {
      email?: string;
      phone?: string;
      phoneCode?: string;
    }
  ): Promise<void> {
    const data: Record<string, string> = {};
    if (resetUsing === 'email') {
      data['reset_using'] = 'email';
      if (params.email) data['email'] = params.email;
    }
    if (resetUsing === 'phone') {
      data['reset_using'] = 'mobile_code';
      if (params.phone) data['mobile'] = params.phone;
      if (params.phoneCode) data['mobile_code'] = params.phoneCode;
    }

    await this.apiService.post('/api/auth/send_reset_otp', data, {
      authenticate: false,
    });
  }

  async loginWithPassword(
    loginUsing: string,
    password: string,
    params: {
      username?: string;
      email?: string;
      phone?: string;
      phoneCode?: string;
    }
  ): Promise<Record<string, any>> {
    const data: Record<string, string> = {
      password: password,
    };
    if (loginUsing === 'username') {
      data['login_using'] = 'username';
      if (params.username) data['username'] = params.username;
    }
    if (loginUsing === 'email') {
      data['login_using'] = 'email';
      if (params.email) data['email'] = params.email;
    }
    if (loginUsing === 'phone') {
      data['login_using'] = 'mobile_code';
      if (params.phone) data['mobile'] = params.phone;
      if (params.phoneCode) data['mobile_code'] = params.phoneCode;
    }

    const response = await this.apiService.post('/api/auth/login', data, {
      authenticate: false,
    });
    const json = response.data as Record<string, any>;
    return json;
  }

  async loginWithOtp(
    loginUsing: string,
    otp: string,
    params: {
      email?: string;
      phone?: string;
      phoneCode?: string;
    }
  ): Promise<Record<string, any>> {
    const data: Record<string, string> = {
      otp: otp,
    };
    if (loginUsing === 'email') {
      data['login_using'] = 'email';
      if (params.email) data['email'] = params.email;
    }
    if (loginUsing === 'phone') {
      data['login_using'] = 'mobile_code';
      if (params.phone) data['mobile'] = params.phone;
      if (params.phoneCode) data['mobile_code'] = params.phoneCode;
    }

    const response = await this.apiService.post(
      '/api/auth/login_with_otp',
      data,
      {
        authenticate: false,
      }
    );
    const json = response.data as Record<string, any>;
    return json;
  }

  async checkResetOtp(
    resetUsing: string,
    otp: string,
    params: {
      email?: string;
      phone?: string;
      phoneCode?: string;
    }
  ): Promise<void> {
    const data: Record<string, string> = {
      otp: otp,
    };
    if (resetUsing === 'email') {
      data['reset_using'] = 'email';
      if (params.email) data['email'] = params.email;
    }
    if (resetUsing === 'phone') {
      data['reset_using'] = 'mobile_code';
      if (params.phone) data['mobile'] = params.phone;
      if (params.phoneCode) data['mobile_code'] = params.phoneCode;
    }

    await this.apiService.post('/api/auth/check_reset_otp', data, {
      authenticate: false,
    });
  }

  async resetPassword(
    resetUsing: string,
    password: string,
    passwordConfirm: string,
    otp: string,
    params: {
      email?: string;
      phone?: string;
      phoneCode?: string;
    }
  ): Promise<void> {
    const data: Record<string, string> = {
      otp: otp,
      password: password,
      password_confirmation: passwordConfirm,
    };
    if (resetUsing === 'email') {
      data['reset_using'] = 'email';
      if (params.email) data['email'] = params.email;
    }
    if (resetUsing === 'phone') {
      data['reset_using'] = 'mobile_code';
      if (params.phone) data['mobile'] = params.phone;
      if (params.phoneCode) data['mobile_code'] = params.phoneCode;
    }

    await this.apiService.post('/api/auth/reset_password', data, {
      authenticate: false,
    });
  }

  async logout(): Promise<Record<string, any>> {
    const response = await this.apiService.post('/api/auth/logout', {});
    const json = response.data as Record<string, any>;
    return json ?? {};
  }

  async loginWithSsoToken(token: string): Promise<Record<string, any>> {
    const data: Record<string, string> = {
      sso_token: token,
    };

    const response = await this.apiService.post('/api/auth/sso_login', data, {
      authenticate: true,
    });
    const json = response.data as Record<string, any>;
    return json;
  }

  async ssoLogout(token: string): Promise<boolean> {
    const data: Record<string, string> = {
      sso_token: token,
    };

    await this.apiService.post('/api/auth/sso_logout', data);

    return true;
  }

  async requestDemoView(): Promise<DemoRequest> {
    const response = await this.apiService.get('/api/request_demo', {
      authenticate: false,
    });
    const json = response.data as Record<string, any>;
    return DemoRequest.fromJson(json);
  }

  async requestDemoSubmit({
    name,
    email,
    mobile,
    country,
    plan,
    industry,
  }: {
    name: string;
    email: string;
    mobile: string;
    country?: number;
    plan?: number;
    industry?: number;
  }): Promise<void> {
    const data: Record<string, string> = {
      name: name,
      email: email,
      mobile: mobile,
      platform: 'App Web',
    };

    if (country) {
      data['country_id'] = country.toString();
    }
    if (industry) {
      data['industry'] = industry.toString();
    }
    if (plan) {
      data['compensation_plan'] = plan.toString();
    }

    await this.apiService.post('/api/request_demo', data, {
      authenticate: false,
    });
  }
}
